import * as React from "react";
import { getBioKeywords } from "@ds-services/nlp/getwords";
import mockSpotyTracks from "../../testdata/spotify-tracks.json";

/** === ☠ ☠ ☠ ===>

TODO: Use SDK to fetch the tracks

<=== ☠ ☠ ☠ === **/
// <=== 💸  MUI Stuff  💸 ===>
import { dotaSpottoTheme } from "@ds-components/General/dotaspottotheme";
import { Box, Container, Button, ThemeProvider, Backdrop, CircularProgress, Drawer, AppBar, Toolbar, Breakpoint, Typography } from "@mui/material";
import AlbumIcon from '@mui/icons-material/Album';
import InterpreterModeIcon from '@mui/icons-material/InterpreterMode';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import {
	DataGrid,
	GridColDef,
	GridRenderCellParams,
	GridRowSelectionModel,
} from "@mui/x-data-grid";
import type { } from "@mui/x-data-grid/themeAugmentation";
import { TrackMatcher } from "@ds-services/spotify/helpers";
import type { PlaylistStatus } from "@ds-components/Main/heroselect";
import { HeroLookup } from "@ds-types/dotes";
import { SpotifySearchResults, SpotifyPlaylist } from "@ds-types/spotify";
import SpotifyActions from "@ds-components/Playlist/spotifyactions";
import SpotifyModal from "@ds-components/Playlist/dotaspottomodal";
import type { Track } from "@spotify/web-api-ts-sdk";
import { useAuthStore } from "@/store/authStore";


/** === ☠ ☠ ☠ ===>

Setting the Grid up

<=== ☠ ☠ ☠ === **/
export interface DotaHeroSpotifyAuth {
	thehero: {
		status: string;
		payload: HeroLookup;
	};
	checkPlaylistVisible: (show?: PlaylistStatus) => PlaylistStatus;
	seePlaylistStatus: PlaylistStatus;
	userScreenSize: Breakpoint | null;
}

export interface SpotifyTingState {
	tracks: SpotifySearchResults["tracks"]["items"][] | string;
	playing?: boolean;
	tracksloaded?: boolean;
	tracksselected?: boolean | GridRowSelectionModel;
	searchterms: string[] | [];
	playlist: SpotifyPlaylist | boolean | { error: Error };
	showPlaylist: PlaylistStatus;
	playlistChecked: boolean;
	screenSize: Breakpoint | null;
};


declare global {
	interface Window {
		onSpotifyIframeApiReady(something: any): void;
	}
}




class SpotifyRateLimiter {
	maxRequests: number;
	requests: any[];
	windowMs: number;
	constructor(maxRequests = 30) {
		this.maxRequests = maxRequests;  // Maximum requests in 30-second window
		this.requests = [];              // Timestamp log of requests
		this.windowMs = 30 * 1000;       // 30 seconds in milliseconds
	}

	// Instead of filtering twice, combine the operations
	async throttle() {
		const now = Date.now();
		this.requests = this.requests.filter(timestamp => now - timestamp < this.windowMs);

		if (this.requests.length >= this.maxRequests) {
			const oldestRequest = this.requests[0];
			const delayMs = oldestRequest + this.windowMs - now;

			if (delayMs > 0) {
				await new Promise(resolve => setTimeout(resolve, delayMs));
				// Update timestamp after delay without another filter operation
				this.requests.shift(); // Remove oldest request
			}
		}
		this.requests.push(now);
	}

	async getSpotifyResults(term: string, auth: string): Promise<Track[] | undefined> {
		await this.throttle();
		try {
			const spotifyInstance = useAuthStore.getState().spotify;
			if (!spotifyInstance) {
				throw new Error("Spotify client is not available.");
			}
			// Generate random integer between 10 and 50 in increments of 10
			const randomTens = Math.floor(Math.random() * 6) * 10;
			// const spotifyFetchUrl = `https://api.spotify.com/v1/search?type=track&limit=10&q=${term}&offset=${randomTens}`;
			// console.log(`🥮grim__spotifyFetchUrl==> ==> ==>spotifyFetchUrl`);
			// console.log({ grim__spotifyFetchUrl: spotifyFetchUrl });
			const spotifyTracks = await spotifyInstance?.search(term, ["track"], undefined, 10, randomTens)
				.then(async (searchResults) => {

					if (!searchResults) {
						// Handle rate limit response specifically
						const retryAfter = searchResults;
						// const retryAfter = tracks.headers.get('Retry-After');
						if (retryAfter) {
							await new Promise(resolve =>
								setTimeout(resolve, 1000)
							);
							// Retry the request
							return this.getSpotifyResults(term, auth);
						}
						// throw new Error(`HTTP error! status: ${response.status}`);
					}
					return searchResults.tracks.items;
				})
				.catch((error) => {
					// this.auth = null;
					throw error;
				});
			return spotifyTracks;
			// return SpotifyResults;
		} catch (error) {
			console.error(error);
			useAuthStore.getState().showToast("Could not load tracks for this hero. Please try again.", "error");
		}
	};
}

/** === ☠ ☠ ☠ ===>

Tracks

<=== ☠ ☠ ☠ === **/
class SpotifyTing extends React.Component<DotaHeroSpotifyAuth, SpotifyTingState> {
	constructor(props: DotaHeroSpotifyAuth) {
		super(props);
		this.state = {
			tracks: "loading",
			playing: false,
			tracksloaded: false,
			tracksselected: false,
			searchterms: [],
			playlist: false,
			showPlaylist: this.props.checkPlaylistVisible(),
			playlistChecked: false,
			screenSize: null
		};
	}
	/** === ☠ ☠ ☠ ===>
	
	Test Mode Settings
	
	<=== ☠ ☠ ☠ === **/
	getTestMode = async () => {
		// await new Promise(resolve => setTimeout(resolve, 9999999));
		return mockSpotyTracks as unknown as Track[];
	}
	/** === ☠ ☠ ☠ ===>
	
	Component Functions
	
	<=== ☠ ☠ ☠ === **/
	batchSpotifyRequests = async (urls: string[], auth: string) => {
		const spotifyLimiter = new SpotifyRateLimiter(30); // 30 requests per 30 seconds
		const results: Track[] | null = [];

		for (const url of urls) {
			try {
				const result = await spotifyLimiter.getSpotifyResults(url, auth);
				if (result) {
					results.push(...result);
				}
			} catch (error) {
				console.error(`Error fetching ${url}:`, error);
			}
		}
		return results;
	}

	spotifySearch = async (herodata: HeroLookup, auth: string) => {

		if (import.meta.env.VITE_TESTMODE) return this.getTestMode();
		// For batch requests
		const searchTerms: string[] = [];

		// <=== 💸  What we looking for?  💸 ===>
		const heroName = herodata.displayName;
		if (heroName) searchTerms.push(heroName);
		if (!herodata.bio) return;
		await getBioKeywords(herodata.bio).then((data) => {
			searchTerms.push(...data as string[]);
		}).catch((error) => {
			console.error(`🥮grim__error==> ==> ==>error`);
			console.error({ grim__error: error });
		})

		if (searchTerms.length > 0) {
			this.setState({
				searchterms: searchTerms,
				tracks: "almost"
			});
		}
		// <=== 💸  What we looking for?  💸 ===>
		const getTracks = await this.batchSpotifyRequests(searchTerms, auth);
		return getTracks;
	};

	getTracks = () => {
		return this.state.tracks;
	};
	getTracksLoaded = () => {
		return this.state.tracksloaded;
	};
	getPlaylist = (createdplaylist: any) => {
		this.setState({
			playlist: createdplaylist
		})
	}

	updateTracks = (userdata: DotaHeroSpotifyAuth) => {
		const trackSearch = this.spotifySearch(
			userdata.thehero.payload,
			""
		);

		trackSearch
			.then((herotracks) => {

				if (herotracks) {

					const layoutTracks = herotracks;

					const foundAlbum: Set<string> = new Set();

					const getSpotifyTracksArray: Track[] = layoutTracks
						.map((results) => results).flat(20);

					const layoutTracksFiltered = getSpotifyTracksArray
						.filter((track: Track) => {
							if (!foundAlbum.has(track.album.id)) {
								foundAlbum.add(track.album.id);
								return true;
							}
							return false;
						});
					// const foundTrackNames: Set<string> = new Set();
					const useTrackMatcher = new TrackMatcher();
					const uniqueTracks = useTrackMatcher.filterDuplicates(layoutTracksFiltered);

					this.setState({
						tracks: uniqueTracks,
					});
				}
			})
			.catch((error) => {
				console.error(`🥮grim__error==> ==> ==>error`);
				console.error({ grim__error: error });
				this.setState({
					tracks: "error",
				});
				return <>
					Error
				</>;
			});
	};
	// addAudioListeners = () => {
	// 	const audioplayers = document.querySelectorAll("audio");
	// 	if (audioplayers) {
	// 		audioplayers.forEach((player) => {
	// 			player.addEventListener("play", (event) => {
	// 				audioplayers.forEach((audioel: HTMLAudioElement) => {
	// 					if (audioel !== event.target) {
	// 						audioel.pause();
	// 					} else {
	// 						audioel.play();
	// 					}
	// 				});
	// 			});
	// 		});
	// 	}
	// };
	componentDidMount() {
		const { thehero, userScreenSize } = this.props;
		const herostatus = thehero.status;

		if (herostatus === "loaded") {
			this.updateTracks(this.props);
			const script = document.createElement('script');
			script.src = 'https://open.spotify.com/embed/iframe-api/v1';
			script.async = true;
			document.head.appendChild(script);
		}
		if (userScreenSize) {
			this.setState({
				screenSize: userScreenSize
			})
		}
	}
	componentDidUpdate(prevProps: any) {
		const { thehero } = this.props;
		const { tracks, playlistChecked } = this.state;

		if (prevProps.thehero.payload !== thehero.payload) {
			this.updateTracks(this.props);
		}
		if (tracks === "loading") {
			this.props.checkPlaylistVisible({
				status: false,
				visible: false,
				loading: true
			})
		}
		if ((tracks === "loading" || tracks === "almost") && playlistChecked) {
			this.setState({
				playlistChecked: false
			})
		}
		if (tracks !== "loading" && tracks !== "almost" && tracks !== "error" && !playlistChecked) {
			const showLoadPlaylist = {
				status: true,
				visible: true,
			}
			this.setState({
				showPlaylist: showLoadPlaylist,
				playlistChecked: true
			});
			this.props.checkPlaylistVisible(showLoadPlaylist);

		}
	}
	render() {
		const { thehero } = this.props;
		const { tracks, searchterms, tracksselected, screenSize } = this.state;
		const isSmOrXs = screenSize ? ["xs", "sm"].includes(screenSize) : false;
		const handleHidePlaylist = () => {
			const hidePlaylist = {
				status: true,
				visible: false
			};
			this.setState({
				showPlaylist: hidePlaylist,
			});
			this.props.checkPlaylistVisible(hidePlaylist);
		};
		const getSpotyTracks = this.getTracks();
		//Grid Things
		const mobileGridStyles = {
			RowContainer: {
				display: 'flex',
				alignItems: 'center',
				gap: 1,
				width: '100%',
			},
			IconsBox: {
				backgroundColor: "#A72714",
				position: "sticky",
				left: 0, // change from -1px to 0 to keep it flush and avoid negative offset issues
				zIndex: 1, // make sure it stays above content
				borderRight: '1px solid black',
				paddingRight: '4px',
				height: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: '24px', // fixed width for consistency
				flexShrink: 0,
			},
			TypographyBox: {
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
				flex: 1, // let it take remaining space
				minWidth: 0 // necessary with flex to allow shrinking
			}

		};
		const columns: GridColDef[] = isSmOrXs ? [
			{
				field: "album.images[0].url",
				headerName: "Cover",
				sortable: false,
				width: 100,
				renderCell: (params: GridRenderCellParams<any>) => (
					<img
						src={params?.row?.album?.images[0]?.url}
						style={{
							width: "100%",
							height: "auto",
						}}
					/>
				),
			},
			{
				field: "artist.name",
				headerName: "Artist/Track/Album",
				width: 200,
				flex: 1,
				renderCell: (params: GridRenderCellParams<any>) => (
					<>
						<Box sx={{
							lineHeight: 1.1,
							textAlign: "left",
							height: '100%',
							overflow: 'auto',
							// display: 'flex',
							// flexFlow: 'column nowrap',
							// justifyContent: 'center',
							// alignItems: 'stretch'
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'
						}}>
							<Box sx={mobileGridStyles.RowContainer}>
								<Box sx={mobileGridStyles.IconsBox}>
									<InterpreterModeIcon sx={{
										color: dotaSpottoTheme.primary,
										filter: 'brightness(0.42)',
										height: '20px',
									}} />
								</Box>
								<Box sx={mobileGridStyles.TypographyBox}>
									<Typography sx={{
										fontFamily: dotaSpottoTheme.font2,
										fontSize: '1rem',
										fontWeight: 'bold',
										lineHeight: 1.1,
									}}>
										{params.row?.artists[0]?.name}
									</Typography>
								</Box>
							</Box>
							<Box sx={mobileGridStyles.RowContainer}>
								<Box sx={mobileGridStyles.IconsBox}>
									<AudiotrackIcon sx={{
										color: dotaSpottoTheme.primary,
										filter: 'brightness(0.42)',
										height: '20px',
									}} />
								</Box>
								<Box sx={mobileGridStyles.TypographyBox}>
									<Typography sx={{
										fontFamily: dotaSpottoTheme.font,
										lineHeight: 1.1
									}}>
										{params.row?.name}
									</Typography>
								</Box>
							</Box>
							<Box sx={mobileGridStyles.RowContainer}>
								<Box sx={mobileGridStyles.IconsBox}>
									<AlbumIcon sx={{
										color: dotaSpottoTheme.primary,
										filter: 'brightness(0.42)',
										height: '20px',
									}} />
								</Box>
								<Box sx={mobileGridStyles.TypographyBox}>
									<Typography sx={{
										fontFamily: dotaSpottoTheme.font,
										lineHeight: 1.1
									}}>
										{params.row?.album?.name}
									</Typography>
								</Box>
							</Box>
						</Box>
					</>
				)
			},
		] : [
			{
				field: "album.images[0].url",
				headerName: "Album Art",
				sortable: false,
				width: 100,
				renderCell: (params: GridRenderCellParams<any>) => (
					<img
						src={params?.row?.album?.images[0]?.url}
						style={{
							width: "100%",
							height: "auto",
						}}
					/>
				),
			},
			{
				field: "album.name",
				headerName: "Album Name",
				flex: 0.25,
				// minWidth: 400,
				width: 100,
				sortable: true,
				valueGetter: (value, row: SpotifySearchResults["tracks"]["items"]) => {
					return `${row?.album?.name}`
				},
			},
			{
				field: "artist.name",
				headerName: "Artist",
				flex: 0.5,
				// minWidth: 400,
				width: 100,
				sortable: true,
				valueGetter: (value, row: SpotifySearchResults["tracks"]["items"]) => {
					return `${row?.artists[0]?.name}`
				}
				// rowSpanValueGetter: (params: any) => ,
			},
			{
				field: "name",
				headerName: "Track Name",
				flex: 0.7,
				minWidth: 400,
				sortable: true,
				// width: 500,
			},
		];
		return (
			<>
				{
					getSpotyTracks === "loading" && (
						<ThemeProvider theme={dotaSpottoTheme}>
							<Backdrop
								sx={(theme) => ({
									color: '#fff',
									zIndex: theme.zIndex.drawer + 1,
									flexFlow: 'column nowrap',
									gap: 2,
									// backgroundColor: 'rgba(0,0,0,0.25)',
								})}
								open={true}
							>
								<Box>
									Warming up
								</Box>
								<Box>
									<CircularProgress color="inherit" />
								</Box>

							</Backdrop>
						</ThemeProvider>
					)
				}
				{
					getSpotyTracks === "almost" && (
						<ThemeProvider theme={dotaSpottoTheme}>
							<Backdrop
								sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
								open={true}
							>
								<Box>
									Compiling tracks {searchterms.length > 0 && `for ${searchterms[0]}`}
								</Box>
								<CircularProgress color="inherit" />
							</Backdrop>
						</ThemeProvider>
					)
				}
				{
					getSpotyTracks === "error" && (

						<ThemeProvider theme={dotaSpottoTheme}>
							<>
								<SpotifyModal
									playlist={false}
									text="Could not load tracks for this hero. Please try again."
								/>
							</>
						</ThemeProvider>
					)
				}
				{
					getSpotyTracks !== "loading" && getSpotyTracks !== "almost" && getSpotyTracks !== "error" && (
						<>
							<Drawer
								variant="persistent"
								anchor={(screenSize === "xs" || screenSize === "sm") ? `bottom` : `right`}
								elevation={8}
								sx={{
									width: '0',
									flexShrink: 0,
									[`& .MuiDrawer-paper`]: {
										width: {
											xs: '100%',
											md: '65vw'
										},
										boxSizing: 'border-box',
									},
									[`&.show`]: {
										width: {
											xs: '100%',
											md: '65vw'
										},
										position: {
											xs: 'absolute',
											md: 'relative'
										}
									},
								}}
								open={this.props.seePlaylistStatus.visible}
								className={this.props.seePlaylistStatus.visible ? "show" : "hide"}
							>
								<AppBar position="sticky">
									<Toolbar>
										<Box sx={{ flexGrow: 1 }}>
											<SpotifyActions
												hero={this.props.thehero.payload ?? "No Hero"}
												alltracks={tracks}
												selectedtracks={tracksselected}
												getPlaylist={this.getPlaylist}
												searchterms={searchterms}
												isMobile={isSmOrXs}
											/>
										</Box>
										<Box>
											<Button
												size="large"
												onClick={handleHidePlaylist}
												sx={{
													marginLeft: "auto",
													backgroundColor: dotaSpottoTheme.primary,
													color: "#FFF",
													fontFamily: dotaSpottoTheme.font2,
													fontWeight: 700,
													height: 40,
													// border: `1px solid black`,
													":hover, :focus": {
														backgroundColor: dotaSpottoTheme.primary,
														color: "#FFF"
													},
												}}
											>
												Close
											</Button>
										</Box>
									</Toolbar>
								</AppBar>
								<Box sx={{ width: "100%" }}>
									<ThemeProvider theme={dotaSpottoTheme}>
										<DataGrid
											loading={getSpotyTracks === "loading"}
											rows={getSpotyTracks as Track[]}
											columns={columns}
											getRowId={(row) => row.uri}
											rowHeight={80}
											disableVirtualization
											disableColumnResize={screenSize ? ["xs", "sm"].includes(screenSize) : false}
											checkboxSelection
											disableRowSelectionOnClick
											onRowSelectionModelChange={(selectedTracks) => {
												if (selectedTracks.length > 0) {
													this.setState({
														tracksselected: selectedTracks,
													});
												} else {
													this.setState({
														tracksselected: false,
													});
												}
											}}

										/>
									</ThemeProvider>
								</Box>
								{this.state.playlist && typeof this.state.playlist === 'object' && !('error' in this.state.playlist) && (
									<Container>
										<h1>SPOTIFY PLAYLIST</h1>
										<SpotifyModal
											playlist={{
												thehero: thehero.payload,
												theplaylist: this.state.playlist as SpotifyPlaylist
											}}
										/>
									</Container>
								)}
								{/* {this.state.playlist && typeof this.state.playlist === 'object' && ('error' in this.state.playlist) && (
									<Container>
										<h1>SPOTIFY PLAYLIST</h1>
										<SpotifyModal
											playlist={false}
											text={this.state.playlist.error.message}
										/>
									</Container>
								)} */}
							</Drawer>
						</>
					)
				}
			</>
		);
	}
}

export default SpotifyTing;
